import { getCalculateSessionId } from '../../../comparator-graph-legacy-api';
import {
  HealthCare_SupplierReview,
  HomeProduct,
  ProductCommission,
  ProductIncentiveDetail,
  ProductSupplier,
  ProductSupplierFeature,
} from '../../../graphProduct';
import {
  IProduct,
  basicHomeIProduct,
  basicHomeProduct,
  basicHomeSupplier,
  getUspsBySellingPointsByOrderType,
} from '../../../product';
import { CommonTechnical } from '../travel/types';
import {
  HealthCare_PolicyDetails,
  HealthCare_PolicyDetailsStructure,
} from './types';
import { getAcceptanceValues, getDocuments, getPolicyDetails } from './utils';

export class CBHealthQueryProvider {
  public validatePostcodeVariables(postcode: string) {
    return { postalCode: postcode };
  }

  public validateAddressVariables(
    postcode: string,
    houseNumber: number,
    houseNumberAddition?: string
  ): object {
    return {
      postalCode: postcode,
      houseNumber,
      houseNumberAddition,
    };
  }

  public validateDynamicFormVariables(
    formName: string,
    frontEndName: string,
    clientName: string,
    values: any
  ) {
    return {
      formName,
      frontEndName,
      clientName,
      values,
    };
  }

  public validateEmailVariables(
    comparisonId: string | number,
    email: string,
    productType: string
  ): object {
    return {
      comparisonId,
      email,
      productType,
    };
  }

  public validateCustomerDataVariables(
    firstName: string,
    email: string,
    productType: string
  ): object {
    return {
      email,
      productType,
      firstName,
    };
  }

  public calculationInputVariables(comparisonId: string) {
    return {
      comparisonId: comparisonId,
    };
  }

  public validateHealthCareVariables(
    searchCriteria: object,
    selectedFilters: object,
    credentials: object
  ): object {
    const filters = { ...selectedFilters };
    const paymentTermInMonths =
      filters['showPriceMode']?.[0] === 'month' ? 1 : 12 || 1;

    const childrenData = [
      {
        name: 'childBirthdate1',
        value: searchCriteria['childBirthdate1'],
      },
      {
        name: 'childBirthdate2',
        value: searchCriteria['childBirthdate2'],
      },
      {
        name: 'childBirthdate3',
        value: searchCriteria['childBirthdate3'],
      },
      {
        name: 'childBirthdate4',
        value: searchCriteria['childBirthdate4'],
      },
      {
        name: 'childBirthdate5',
        value: searchCriteria['childBirthdate5'],
      },
      {
        name: 'childGender1',
        value: searchCriteria['childGender1'],
      },
      {
        name: 'childGender2',
        value: searchCriteria['childGender2'],
      },
      {
        name: 'childGender3',
        value: searchCriteria['childGender3'],
      },
      {
        name: 'childGender4',
        value: searchCriteria['childGender4'],
      },
      {
        name: 'childGender5',
        value: searchCriteria['childGender5'],
      },
    ];

    delete filters['showPriceMode'];
    delete filters['rivm'];
    delete filters['freeChoiceOfCaregiver'];
    delete filters['providerName'];
    const coverageValues = Object.keys(filters).map((elem) => {
      return {
        name: elem,
        value: filters[elem]?.[0],
      };
    });

    const partyGiversValue = searchCriteria['careGivers']
      ? JSON.parse(searchCriteria['careGivers'])
      : [];

    const partyCaregivers = partyGiversValue.map((giver) => ({
      externalSourceName: giver.externalSourceName,
    }));

    let calculateParameters = {
      deductibleAmount: Number(searchCriteria['deductibleAmount']),
      birthDate: searchCriteria['birthdate'],
      postalCode: searchCriteria['postcode'],
      gender: searchCriteria['gender'],
      partner: {
        birthDate: searchCriteria['partnerBirthdate'],
        gender: searchCriteria['partnerGender'],
      },
      children: childrenData.filter((elem) => Boolean(elem.value)),
      coverages: coverageValues.filter((elem) => elem.value !== '-1'),
      partyCaregivers,
      currentlyStudying: searchCriteria['isStudent'],
    };

    return this.calculateHealthCareVariables(
      calculateParameters,
      {
        sessionId: getCalculateSessionId(),
        clientId: credentials['clientId'],
      },
      paymentTermInMonths
    );
  }

  public calculateHealthCareVariables(
    data,
    { clientId, sessionId },
    paymentTermInMonths = 1
  ): object {
    return getPolicyDetails(data, { clientId, sessionId }, paymentTermInMonths);
  }

  public validateSuppliersVariables(
    result: HealthCare_PolicyDetailsStructure
  ): object {
    const productKrns = result.policyPolicyDetails?.map((item) => {
      return item.productCode;
    });

    return {
      productKrns: productKrns,
      productCategory: 'HEALTH_CARE',
    };
  }

  public getIdsFromProducts(products: HomeProduct[]): {
    parentProductIds: string[];
    productIds: string[];
  } {
    let parentProductIds: string[] = [];
    let productIds: string[] = [];

    for (const product of products) {
      if (product.dataProviderKrn.includes('moneyview')) {
        if (product.productType === 'insurance/package') {
          parentProductIds.push(product.externalId);
        } else {
          productIds.push(product.externalId);
        }
      }
    }

    return {
      parentProductIds,
      productIds,
    };
  }

  public featuresVariables(
    parentProductIds: string[],
    productIds: string[],
    after: string | undefined
  ): object {
    return {
      where: {
        and: [
          {
            or: [
              {
                parentProductId: {
                  in: parentProductIds.map((id) => parseInt(id)),
                },
              },
              {
                productId: {
                  in: productIds.map((id) => parseInt(id)),
                },
              },
            ],
          },
          {
            externalFeatureId: {
              in: [
                25704, 25304, 25278, 25297, 25239, 25237, 25244, 25304, 25278,
                25297, 25342, 25264, 25281, 25269, 25274, 25377, 25399, 25305,
                25239, 25237,
              ],
            },
          },
        ],
      },
      after,
    };
  }

  private handleIncentives = ({
    productIncentiveDetails,
    productIncentives,
  }) => {
    if (productIncentives === null || productIncentiveDetails.length === 0) {
      return null;
    }
    for (const incentiveItem of productIncentives) {
      return productIncentiveDetails.find(
        (item) => item.krn === incentiveItem.productIncentiveDetailsKrn
      );
    }
  };

  public formatHealthProduct(
    credentials: any,
    packages: HealthCare_PolicyDetails[],
    products: HomeProduct[],
    suppliers: ProductSupplier[],
    suppliersReviews: HealthCare_SupplierReview[],
    commonTechnical: CommonTechnical[],
    productIncentiveDetails: ProductIncentiveDetail[],
    productCommissions: ProductCommission[]
  ): IProduct[] {
    return packages
      .filter((item) => item.totalInstallmentAmount !== null)
      .map((policyDetails) => {
        const {
          customProperties,
          productCode,
          productName,
          totalInstallmentAmount,
          coverageBasicHealths,
          coverageAdditionalHealths,
          coverageDentistHealths,
          companyNameDescription,
          companyGroupName,
          contractNumber,
          declarationMethods,
          expectedAcquisitionCommissionPartnerAmount,
          partyPolicyHolders,
          policyDeliveryMethods,
          grossPremiumInstallment,
          affiliateUrl,
          orderFormId,
          zorgWebServiceProviderScore,
          zorgWebFreeCareChoiceScore,
          zorgWebFreeHospitalScore,
          zorgWebFreeHospitalDescription,
          zorgWebWeightedPurchaseScore,
          zorgWebWeightedReviewValuation,
          zorgWebWeightedServiceScore,
          zorgWebWeightedRating,
          zorgWebBehaviours,
          zorgWebAddedValueScore,
          tags,
        } = policyDetails;

        const product: HomeProduct = {
          ...basicHomeProduct,
          ...products.find((p) => p.krn === productCode),
        };

        const supplier: ProductSupplier = {
          ...basicHomeSupplier,
          ...suppliers.find((s) => s.krn === customProperties?.supplierKrn),
        };

        const productCommission = {
          ...{
            krn: '',
            renewalCommissionInstallmentAmountPartner: null,
            commissionConfigs: [
              {
                renewalCommissionInstallmentNumberOfMonths: null,
                renewalCommissionInstallmentAmountLink: null,
              },
            ],
          },
          ...productCommissions.find((p) => {
            const productKrnMatch = product.krn.match(/^(.*?)(?:\+.*)?$/);
            const productBaseKrn = productKrnMatch
              ? productKrnMatch[1]
              : product.krn;

            const pKrnMatch = p.krn.match(/^(.*?)(?:\+.*)?$/);
            const pBaseKrn = pKrnMatch ? pKrnMatch[1] : p.krn;

            return pBaseKrn === productBaseKrn;
          }),
        };

        const {
          affiliateUrl: productAffiliateUrl,
          insuranceCardUrl,
          orderType,
          productType,
          termsAndConditionsUrl,
          productIncentives,
          customAttributes,
          externalId,
        } = product;

        const {
          about,
          advantages,
          disadvantages,
          features,
          howItWorks,
          krn,
          name,
          nameLogoUrl,
          sellingPoints,
          sustainabilityScore,
          taskDivisions,
        } = supplier;

        const reviews = suppliersReviews.find(
          (el) => el.supplierKrn === krn
        )?.review;

        let visibleAffiliateUrl = '';

        if (orderType === 'AFFILIATE') {
          visibleAffiliateUrl = affiliateUrl || productAffiliateUrl;
        }

        const deliveryMethods = policyDeliveryMethods?.filter(
          ({ isSupported }) => Boolean(isSupported)
        );

        const acceptanceValues = getAcceptanceValues(
          coverageAdditionalHealths,
          coverageDentistHealths
        );

        const waitingTimeTooltip = acceptanceValues?.filter(
          (el) => el.forWho === 'NIEUW_VERZEKERDEN' && el.level === 'WACHTTIJD'
        );
        const renewalCommissionInstallmentAmountPartner =
          productCommission?.renewalCommissionInstallmentAmountPartner;

        return {
          ...basicHomeIProduct,
          affiliateUrl: visibleAffiliateUrl,
          commonTechnical: [
            {
              senderId: credentials.clientId,
              sessionId: '',
              transactionReference:
                commonTechnical[0].transactionReference || '',
              accessCode: commonTechnical[0].accessCode || '',
            },
          ],
          bigProductFeature: {
            ...basicHomeIProduct.bigProductFeature,
            decimals: 0,
            label: 'Eigen risico',
            hideIfZero: false,
            variant: 'priceMedium',
            value: {
              month: coverageBasicHealths?.[0].deductibleAmount,
              year: coverageBasicHealths?.[0].deductibleAmount,
            },
          },
          characteristics: this.buildCharacteristics(features || []),
          customAttributes,
          customProperties,
          externalId,
          id: `${productCode}`,
          image: nameLogoUrl,
          orderType,
          priceInfo: {
            ...basicHomeIProduct.priceInfo,
            initial: totalInstallmentAmount,
            total: totalInstallmentAmount,
          },
          productCode: productCode || '',
          productType: productType || '',
          productName: productName || '',
          termsAndConditionsUrl,
          testScores: [
            {
              score: zorgWebWeightedRating,
              zorgWebServiceProviderScore,
              zorgWebFreeCareChoiceScore,
              zorgWebFreeHospitalScore,
              zorgWebFreeHospitalDescription,
              zorgWebWeightedPurchaseScore,
              zorgWebWeightedReviewValuation,
              zorgWebWeightedServiceScore,
              zorgWebWeightedRating,
              zorgWebAddedValueScore,
            },
          ],
          zorgWebWeightedReviewValuation: parseFloat(
            zorgWebWeightedReviewValuation.toFixed(2)
          ),
          sustainabilityScore: sustainabilityScore || '0',
          // ? `${sustainabilityScore[0].score}`
          // : '0',
          prosAndCons: {
            cons: {
              ...basicHomeIProduct.prosAndCons.cons,
              title1: disadvantages?.[0]?.description || '',
              title2: disadvantages?.[1]?.description || '',
            },
            pros: {
              ...basicHomeIProduct.prosAndCons.cons,
              title1: advantages?.[0]?.description || '',
              title2: advantages?.[1]?.description || '',
            },
          },
          provider: {
            ...basicHomeIProduct.provider,
            description: about,
            howItWorks,
            insuranceCardUrl,
            label: name,
            name,
            sellingPoints,
            sustainabilityScore: sustainabilityScore || '',
            // ? `${sustainabilityScore[0].score}`
            // : '',
            taskDivisions,
            termsAndConditionsUrl,
            id: supplier.krn,
            customAttributes: supplier.customAttributes,
          },
          purchasableProduct: orderType !== 'NOT_ORDERABLE',
          testScore: zorgWebWeightedRating || 0,
          usps: getUspsBySellingPointsByOrderType(sellingPoints, orderType),
          totalInstallmentAmount,
          type: 'health',
          rivm:
            productType === 'insurance/healthcare/budget'
              ? 'Natura budget'
              : coverageBasicHealths?.[0].rivmType,
          companyNameDescription,
          companyGroupName,
          coverageBasicHealths: {
            productDescription: coverageBasicHealths?.[0].productDescription,
            deductibleAmount: coverageBasicHealths?.[0].deductibleAmount,
            deductibleDiscountAmount:
              coverageBasicHealths?.[0].deductibleDiscountAmount,
            totalInstallmentAmount:
              coverageBasicHealths?.[0].totalInstallmentAmount,
            freeChoiceOfCaregiver: zorgWebFreeHospitalDescription,
          },
          coverageAdditionalHealths,
          coverageDentistHealths,
          documents: getDocuments(
            coverageBasicHealths?.[0],
            coverageAdditionalHealths?.[0],
            coverageDentistHealths?.[0]
          ),
          contractNumber,
          strictestLevelNewInsured: policyDetails.strictestLevelNewInsured,
          declarationMethods,
          expectedAcquisitionCommissionPartnerAmount,
          partyPolicyHolders,
          deliveryMethods,
          grossPremiumInstallment,
          orderFormId,
          waitingTimeTooltip,
          zorgWebBehaviours,
          incentive: this.handleIncentives({
            productIncentiveDetails,
            productIncentives,
          }),
          reviews,
          tags: tags !== null ? tags[0] : tags,
          commissionTransparency: {
            renewalCommissionInstallmentAmountPartner:
              renewalCommissionInstallmentAmountPartner != null
                ? renewalCommissionInstallmentAmountPartner
                    .toFixed(2)
                    .replace('.', ',')
                : null,
            renewalCommissionInstallmentNumberOfMonths:
              productCommission?.commissionConfigs?.[0]
                ?.renewalCommissionInstallmentNumberOfMonths,
            renewalCommissionInstallmentAmountLink:
              productCommission?.commissionConfigs?.[0]
                ?.renewalCommissionInstallmentAmountLink,
          },
        };
      });
  }

  public orderHealthcareVariables(
    comparisonId: string,
    chosenProductKrn: string,
    clientId: string,
    affiliateIncentive?: {
      userDesiresIncentive: boolean;
      callName?: string;
      prefixes?: string;
      surname?: string;
      birthDate?: string;
      email?: string;
      postalCode?: string;
      houseNumber?: string;
      houseNumberAddition?: string;
      gender?: string;
    }
  ): object {
    const policy = affiliateIncentive?.userDesiresIncentive
      ? {
          userDesiresIncentive: true,
          objectBuildings: {
            addresses: {
              postalCode: affiliateIncentive?.postalCode,
              houseNumber: affiliateIncentive?.houseNumber,
              houseNumberAddition: affiliateIncentive?.houseNumberAddition,
            },
          },
          partyPolicyHolders: [
            {
              callName: affiliateIncentive?.callName,
              prefixes: affiliateIncentive?.prefixes || null,
              surname: affiliateIncentive?.surname,
              birthDate: affiliateIncentive?.birthDate,
              email: affiliateIncentive?.email,
              genderOrLegalPersonType:
                affiliateIncentive?.gender === 'male' ? 'VM' : 'VV',
            },
          ],
        }
      : { userDesiresIncentive: false };

    return {
      comparisonId: comparisonId,
      chosenProductKrn: chosenProductKrn,
      $frontEndName: 'overstappen',
      sessionId: localStorage.getItem('calculateSessionId'),
      policy: policy,
      clientId,
      googleAnalyticsId: this.getCookie('_ga'),
      googleAnalyticsSessionId: this.getCookie(
        this.getAnalyticsSessionIdCookieName()
      ),
    };
  }

  private buildCharacteristics = (features: ProductSupplierFeature[]) =>
    features
      ? features.map((feature) => {
          return {
            label: feature.name,
            value: feature.value,
          };
        })
      : [];

  public getNearbyCareGiversVariables(postalCode, careProviderIds, packageIds) {
    return {
      query: {
        postalCode,
        careProviderIds,
        packageIds,
      },
    };
  }

  public searchCareGiversVariables(query, postalCode, careGiverType) {
    return {
      query,
      postalCode,
      careGiverType,
    };
  }

  public getCareGiverDetailsVariables(
    externalSourceName,
    postalCode,
    packageIds
  ) {
    return {
      externalSourceName,
      postalCode,
      packageIds,
    };
  }

  public getIncentiveStatusVariables = (email: string) => {
    return {
      email,
      incentiveName: 'bol-incentive-2024',
      transactionStartDate: '2024-10-10',
      transactionEndDate: '2024-11-20',
    };
  };

  public getRegisterIncentiveVariables = (
    firstName: string,
    prefix: string,
    surname: string,
    email: string,
    postcode: string,
    houseNumber: string,
    houseNumberAddition: string,
    birthDate: string,
    transactionDate: string
  ) => {
    return {
      incentiveRegister: {
        incentiveName: 'bol-incentive-2024',
        firstName,
        prefix,
        surname,
        email,
        postcode,
        houseNumber,
        houseNumberAddition,
        birthDate,
        transactionDate,
      },
    };
  };

  private getCookie(cookieName) {
    let cookie = {};
    document.cookie.split(';').forEach((el) => {
      let [key, value] = el.split('=');
      cookie[key.trim()] = value;
    });
    return cookie[cookieName];
  }

  private getAnalyticsSessionIdCookieName = () => {
    const cookieNameStart = document.cookie.substring(
      document.cookie.indexOf('_ga_')
    );
    return cookieNameStart.substring(0, cookieNameStart.indexOf('='));
  };

  public getPeregrineMainApplicantData(fields: any) {
    return {
      postalCode: fields.find((e) => e.name.includes('postcode')).value,
      birthday: fields.find((e) => e.name.includes('birthdate')).value,
    };
  }

  public getPeregrinePartnerData(fields: any) {
    return {
      postalCode: fields.find((e) => e.name.includes('postcode')).value,
      birthday: fields.find((e) => e.name.includes('partnerBirthdate')).value,
    };
  }
}
